/**
 * Zustand Store: RAG Ingest Job Progress Manager
 *
 * Mirrors the securityScanStore pattern for ingest jobs (job_type === 'ingest').
 * Subscribes to /api/v2/stream/job/{job_id} SSE and shows progress in the
 * global activity feed. Persists across route changes.
 *
 * ORGANIZATION SCOPING
 * --------------------
 * An ingest job processes ONE organization's metadata. Like securityScanStore,
 * this held a single slot and a single connection, so a job started in Org A
 * carried its progress into Org B's header.
 *
 * Jobs are partitioned by organization; every handler is bound to the org whose
 * job it serves, so late frames land in that org's slot. The stream stays
 * attached across a switch — the job is running server-side regardless, and the
 * user should find it still going when they return.
 *
 * The subscription ends when:
 * - job_completed event is received (terminal event from backend)
 * - status_update carries status 'completed' or 'failed' (fallback)
 * - The user signs out (reset)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getQueryClient } from '@/dataProvider/reactQuery/context';
import { useActivityStore } from '@/stores/activityStore';
import {
  getActiveOrgId,
  isActiveOrg,
  readOrgSlice,
  registerOrgScopedState,
  writeOrgSlice,
  type OrgId,
} from '@/lib/org-scope';
import {
  getOngoingIngestJob,
  subscribeToIngestJobStream,
  type IngestStatusUpdateEvent,
  type IngestStageEvent,
} from '@/services/security-scanner';

// ============================================================================
// Types
// ============================================================================

export interface IngestJobState {
  isIngesting: boolean;
  progress: number;
  currentStage: string | null;
  error: Error | null;
  jobId: string | null;
  activityId: string | null;
  orgId: string | null;
  userId: string | null;
}

/** One organization's ingest job, plus the live connection feeding it. */
interface OrgIngestSlot {
  state: IngestJobState;
  /** Non-reactive: closes this org's SSE stream. */
  closeConnection: (() => void) | null;
}

interface IngestJobStore {
  /** Per-organization jobs. Not rendered. */
  byOrg: Map<string, OrgIngestSlot>;

  /** The ACTIVE organization's job. Read-only. */
  state: IngestJobState;

  // Actions
  rehydrateFromOngoingJob: (orgId: string, userId: string, jobId: string) => void;
  checkOngoingJobAndRehydrate: (orgId: string, userId: string) => Promise<void>;
  /** Drop the ACTIVE organization's job and disconnect its stream. Sign-out only. */
  reset: () => void;

  // Internal — all addressed by the organization they belong to
  _slot: (orgId: OrgId | null) => OrgIngestSlot;
  _writeSlot: (orgId: OrgId | null, slot: OrgIngestSlot) => void;
  _patchState: (orgId: OrgId | null, updates: Partial<IngestJobState>) => void;
  _handleStatusUpdate: (orgId: OrgId | null, event: IngestStatusUpdateEvent) => void;
  _handleStageStarted: (orgId: OrgId | null, event: IngestStageEvent) => void;
  _handleStageCompleted: (orgId: OrgId | null, event: IngestStageEvent) => void;
  _handleJobCompleted: (orgId: OrgId | null, event: IngestStageEvent) => void;
  _handleError: (orgId: OrgId | null, error: Error) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: IngestJobState = {
  isIngesting: false,
  progress: 0,
  currentStage: null,
  error: null,
  jobId: null,
  activityId: null,
  orgId: null,
  userId: null,
};

// ============================================================================
// Store
// ============================================================================

function emptySlot(): OrgIngestSlot {
  return { state: initialState, closeConnection: null };
}

export const useIngestJobStore = create<IngestJobStore>()(
  devtools(
    (set, get) => ({
      byOrg: new Map(),
      state: initialState,

      _slot: (orgId) => readOrgSlice(get().byOrg, orgId, emptySlot),

      _writeSlot: (orgId, slot) => {
        set((store) => {
          const byOrg = writeOrgSlice(store.byOrg, orgId, slot);
          return isActiveOrg(orgId) ? { byOrg, state: slot.state } : { byOrg };
        });
      },

      _patchState: (orgId, updates) => {
        const slot = get()._slot(orgId);
        get()._writeSlot(orgId, { ...slot, state: { ...slot.state, ...updates } });
      },

      rehydrateFromOngoingJob: (orgId, userId, jobId) => {
        const slot = get()._slot(orgId);

        slot.closeConnection?.();

        const activityId = slot.state.activityId ?? `rag_ingest_rehydrate_${jobId}`;
        get()._writeSlot(orgId, {
          closeConnection: null,
          state: {
            ...initialState,
            isIngesting: true,
            orgId,
            userId,
            activityId,
            jobId,
          },
        });

        useActivityStore.getState().startActivity(
          activityId,
          'rag_ingest',
          'Resuming ingest job...'
        );

        const closeConnection = subscribeToIngestJobStream(
          { job_id: jobId, org_id: orgId, user_id: userId },
          {
            onStatusUpdate: (event) => get()._handleStatusUpdate(orgId, event),
            onStageStarted: (event) => get()._handleStageStarted(orgId, event),
            onStageCompleted: (event) => get()._handleStageCompleted(orgId, event),
            onJobCompleted: (event) => get()._handleJobCompleted(orgId, event),
            onError: (error) => get()._handleError(orgId, error),
            onComplete: () => { console.log('Ingest SSE connection closed'); },
            onDisconnected: () => {
              // Reconnect against THIS org's slot regardless of what is on screen —
              // dropping the stream because the user stepped away would lose the
              // job's progress for good.
              const current = get()._slot(orgId).state;
              if (current.isIngesting && current.jobId === jobId) {
                setTimeout(() => {
                  const later = get()._slot(orgId).state;
                  if (later.isIngesting && later.jobId === jobId) {
                    get().rehydrateFromOngoingJob(orgId, userId, jobId);
                  }
                }, 2_000);
              }
            },
          }
        );

        get()._writeSlot(orgId, { ...get()._slot(orgId), closeConnection });
      },

      checkOngoingJobAndRehydrate: async (orgId, userId) => {
        try {
          const ongoing = await getOngoingIngestJob({ org_id: orgId, user_id: userId });
          if (ongoing?.job_id) {
            get().rehydrateFromOngoingJob(orgId, userId, ongoing.job_id);
            return;
          }
          // Backend may not have created the job yet — retry once after 3s
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const retry = await getOngoingIngestJob({ org_id: orgId, user_id: userId });
          if (retry?.job_id) {
            get().rehydrateFromOngoingJob(orgId, userId, retry.job_id);
          }
        } catch {
          // No ongoing job or API error; nothing to rehydrate
        }
      },

      _handleStatusUpdate: (orgId, event) => {
        const store = get();
        const activityId = store._slot(orgId).state.activityId;

        if (event.status === 'completed') {
          store._handleJobCompleted(orgId, { job_id: event.job_id, org_id: event.org_id, metadata_retrieval_id: '', stage: event.stage ?? 'completed', status: 'completed', progress: 100, message: 'Ingest complete', timestamp: event.timestamp });
          return;
        }

        if (event.status === 'failed') {
          store._handleError(orgId, new Error(event.message || 'Ingest job failed'));
          return;
        }

        // queued / running
        if (activityId) {
          useActivityStore.getState().updateActivity(activityId, {
            progress: event.progress,
            message: event.stage ? `Ingesting... (${event.stage})` : 'Ingesting...',
          });
        }
        store._patchState(orgId, { progress: event.progress, currentStage: event.stage });
      },

      _handleStageStarted: (orgId, event) => {
        const store = get();
        const activityId = store._slot(orgId).state.activityId;

        store._patchState(orgId, { currentStage: event.stage });

        if (activityId) {
          useActivityStore.getState().updateActivity(activityId, {
            progress: event.progress,
            message: event.message || `Starting ${event.stage}...`,
          });
        }
      },

      _handleStageCompleted: (orgId, event) => {
        const store = get();
        const activityId = store._slot(orgId).state.activityId;

        store._patchState(orgId, { currentStage: null, progress: event.progress });

        if (activityId) {
          useActivityStore.getState().updateActivity(activityId, {
            progress: event.progress,
            message: event.message || `${event.stage} complete`,
          });
        }
      },

      _handleJobCompleted: (orgId, event) => {
        const store = get();
        const slot = store._slot(orgId);
        const activityId = slot.state.activityId;

        if (activityId) {
          useActivityStore.getState().completeActivity(activityId, event.message || 'Ingest complete');
          useActivityStore.getState().removeActivity(activityId);
        }

        store._writeSlot(orgId, {
          closeConnection: null,
          state: { ...slot.state, isIngesting: false, progress: 100, currentStage: null },
        });

        slot.closeConnection?.();

        const qc = getQueryClient();
        qc.invalidateQueries({ queryKey: ['securityHealth'] });
        qc.invalidateQueries({ queryKey: ['securityFindings'] });
      },

      _handleError: (orgId, error) => {
        const store = get();
        const slot = store._slot(orgId);
        const activityId = slot.state.activityId;

        console.error('❌ Ingest job failed:', error);

        if (activityId) {
          useActivityStore.getState().failActivity(activityId, error.message);
          useActivityStore.getState().removeActivity(activityId);
        }

        store._writeSlot(orgId, {
          closeConnection: null,
          state: { ...slot.state, isIngesting: false, error },
        });

        slot.closeConnection?.();
      },

      reset: () => {
        const orgId = getActiveOrgId();
        const slot = get()._slot(orgId);
        slot.closeConnection?.();
        if (slot.state.activityId) {
          useActivityStore.getState().removeActivity(slot.state.activityId);
        }
        get()._writeSlot(orgId, emptySlot());
      },
    }),
    { name: 'ingest-job-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/** Re-point `state` at the newly active organization's job. Nothing is stopped. */
registerOrgScopedState({
  name: 'ingestJobStore',
  onOrgChange: (nextOrgId) => {
    useIngestJobStore.setState((store) => ({
      state: readOrgSlice(store.byOrg, nextOrgId, emptySlot).state,
    }));
  },
  onSignOut: shutdownAllIngestJobs,
});

/** Disconnect every organization's ingest stream. Sign-out only. */
export function shutdownAllIngestJobs(): void {
  const { byOrg } = useIngestJobStore.getState();
  for (const slot of byOrg.values()) slot.closeConnection?.();
  useIngestJobStore.setState({ byOrg: new Map(), state: initialState });
}

// ============================================================================
// Selectors
// ============================================================================

export const selectIsIngesting = (store: IngestJobStore) => store.state.isIngesting;
export const selectIngestProgress = (store: IngestJobStore) => store.state.progress;
export const selectIngestStage = (store: IngestJobStore) => store.state.currentStage;
